// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The state of every serial on a filing or change screen, asked as it is entered (design
 * doc §7.5, §10.3): installed on a machine now (and where), used before and since taken
 * off or scrapped (and where it last was), or never filed.
 *
 * The server refuses an installed serial at submit with SERIAL_ALREADY_INSTALLED, which is
 * correct and far too late: by then the operator has scanned forty parts and put the one
 * in question back in the bin. Asked after every entry instead, the answer arrives while
 * the part is still in hand. A serial used before is not refused — a part taken off for a
 * check may go back on — but the operator should know where it has been.
 *
 * One request for the whole set rather than one per serial, so a restored draft is
 * checked in a single round trip; debounced, so typing a serial by hand does not ask after
 * every prefix of it. The answer is filtered to what is on screen *now*, so clearing a
 * field clears its note without waiting for the next response.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/endpoints";
import type { InstalledSerial, UsedSerial } from "@/api/types";
import { isUsableSerial, normalizeSerial } from "./serials";

export const INSTALLED_LOOKUP_DELAY_MS = 300;

export type SerialState =
  | { kind: "checking" }
  | { kind: "free" }
  | { kind: "installed"; hit: InstalledSerial }
  | { kind: "used"; hit: UsedSerial };

export interface SerialStatus {
  /** Normalized serial → where it is installed, for the entered serials that are. */
  installed: Map<string, InstalledSerial>;
  /** Normalized serial → where it last was, for the entered serials used before. */
  used: Map<string, UsedSerial>;
  /** The state of one entered serial; null for a blank or a placeholder. */
  of: (raw: string) => SerialState | null;
}

export function useSerialStatus(raws: string[]): SerialStatus {
  const wanted = useMemo(
    () => [...new Set(raws.filter(isUsableSerial).map(normalizeSerial))].sort(),
    [raws],
  );
  const key = wanted.join("\n");
  const [asked, setAsked] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setAsked(wanted), INSTALLED_LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
    // `key` stands for `wanted`: a new array with the same serials must not restart it.
  }, [key]);

  const lookup = useQuery({
    queryKey: ["installed-serials", asked],
    queryFn: () => api.archives.installedSerials(asked),
    enabled: asked.length > 0,
    // Keep showing the last answer while the next is in flight, so a note does not blink
    // off and on with every scan.
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  return useMemo(() => {
    const current = new Set(wanted);
    const installed = new Map<string, InstalledSerial>();
    const used = new Map<string, UsedSerial>();
    for (const item of lookup.data?.items ?? []) {
      if (current.has(item.serial_normalized)) installed.set(item.serial_normalized, item);
    }
    for (const item of lookup.data?.used_before ?? []) {
      if (current.has(item.serial_normalized)) used.set(item.serial_normalized, item);
    }
    // What the answer on hand covers. A previous answer shown while the next is in flight
    // covers only what it was asked; the rest are still being checked.
    const answered = new Set(
      lookup.data && !lookup.isPlaceholderData ? (lookup.data.checked ?? asked) : [],
    );
    if (lookup.isPlaceholderData) {
      for (const serial of lookup.data?.checked ?? []) answered.add(serial);
    }
    const of = (raw: string): SerialState | null => {
      if (!isUsableSerial(raw)) return null;
      const serial = normalizeSerial(raw);
      const hit = installed.get(serial);
      if (hit) return { kind: "installed", hit };
      const before = used.get(serial);
      if (before) return { kind: "used", hit: before };
      return answered.has(serial) ? { kind: "free" } : { kind: "checking" };
    };
    return { installed, used, of };
  }, [lookup.data, lookup.isPlaceholderData, key, asked]);
}
